import { is } from './type-guards';
import type {
  AiTextStreamEvent,
  EmailCompetitorsStreamEvent,
  EmailPreviewsStreamEvent,
  EmailStreamEvent,
  ErrorStreamEvent,
  InfoStreamEvent,
  OtherStreamEvent,
  PartialMessageStreamEvent,
  StreamEvent,
  UnknownStreamEvent,
} from './types';

type ParseOptions = {
  onEvent: (event: StreamEvent) => void;
};

export type ParseEventStreamServiceApi = {
  parseEventStream: (response: Response, options: ParseOptions) => Promise<void>;
  isErrorStreamEvent: (event: StreamEvent) => event is ErrorStreamEvent;
  isInfoStreamEvent: (event: StreamEvent) => event is InfoStreamEvent;
  isPartialMessageStreamEvent: (event: StreamEvent) => event is PartialMessageStreamEvent;
  isUnknownStreamEvent: (event: StreamEvent) => event is UnknownStreamEvent;
  isEmailStreamEvent: (event: StreamEvent) => event is EmailStreamEvent;
  isAiTextStreamEvent: (event: StreamEvent) => event is AiTextStreamEvent;
  getAiTextStreamChunk: (event: StreamEvent) => { id: string; content: string } | null;
  isEmailPreviewStreamEvent: (event: StreamEvent) => event is EmailPreviewsStreamEvent;
  isEmailCompetitorsStreamEvent: (event: StreamEvent) => event is EmailCompetitorsStreamEvent;
  isOtherStreamEvent: (event: StreamEvent) => event is OtherStreamEvent;
};

const createParseEventStreamService = (): ParseEventStreamServiceApi => {
  const isErrorStreamEvent = (event: StreamEvent): event is ErrorStreamEvent => {
    return event && typeof event === 'object' && event.type === 'error' && is.string(event.data);
  };

  const isInfoStreamEvent = (event: StreamEvent): event is InfoStreamEvent => {
    return (
      event &&
      typeof event === 'object' &&
      event.type === 'info' &&
      is.record(event.data) &&
      typeof event.data.threadId === 'string'
    );
  };

  const isPartialMessageStreamEvent = (event: StreamEvent): event is PartialMessageStreamEvent => {
    return (
      event &&
      typeof event === 'object' &&
      event.type === 'messages/partial' &&
      Array.isArray(event.data) &&
      is.record(event.data[0]) &&
      typeof event.data[0].content === 'string'
    );
  };

  const isUnknownStreamEvent = (event: StreamEvent): event is UnknownStreamEvent => {
    return (
      event &&
      typeof event === 'object' &&
      event.type === 'unknown' &&
      is.record(event.data) &&
      typeof event.data.error === 'string' &&
      typeof event.data.message === 'string'
    );
  };

  const isEmailStreamEvent = (event: StreamEvent): event is EmailStreamEvent => {
    return isPartialMessageStreamEvent(event) && event.data[0].type === 'email';
  };

  const isAiTextStreamEvent = (event: StreamEvent): event is AiTextStreamEvent => {
    return isPartialMessageStreamEvent(event) && event.data[0].type === 'ai';
  };

  const getAiTextStreamChunk = (event: StreamEvent): { id: string; content: string } | null => {
    if (!isAiTextStreamEvent(event)) {
      return null;
    }
    const { id, content } = event.data[0];
    if (!id) {
      return null;
    }
    return { id, content };
  };

  const isEmailPreviewStreamEvent = (event: StreamEvent): event is EmailPreviewsStreamEvent => {
    return isPartialMessageStreamEvent(event) && event.data[0].type === 'email_previews';
  };

  const isEmailCompetitorsStreamEvent = (event: StreamEvent): event is EmailCompetitorsStreamEvent => {
    return isPartialMessageStreamEvent(event) && event.data[0].type === 'competitors';
  };

  const isOtherStreamEvent = (event: StreamEvent): event is OtherStreamEvent => {
    return (
      event &&
      typeof event === 'object' &&
      !(
        event.type === 'info' ||
        event.type === 'messages/partial' ||
        event.type === 'unknown' ||
        event.type === 'error'
      )
    );
  };

  const parseEventStream = async (response: Response, { onEvent }: ParseOptions) => {
    const reader = response.body?.getReader();
    if (!reader) {
      throw new Error('No response body available for streaming');
    }

    const decoder = new TextDecoder();
    let buffer = '';
    let currentEvent: string | null = null;
    let currentData: string[] = [];

    const processBufferedEvent = () => {
      if (!currentEvent || currentData.length === 0) {
        return;
      }

      const raw = currentData.join('\n');
      let parsedData: unknown;

      try {
        parsedData = JSON.parse(raw);
      } catch {
        parsedData = raw;
      }

      onEvent({ type: currentEvent, data: parsedData });

      currentEvent = null;
      currentData = [];
    };

    while (true) {
      const { value, done } = await reader.read();
      if (done) {
        processBufferedEvent();
        break;
      }

      buffer += decoder.decode(value, { stream: true });
      const lines = buffer.split('\n');
      buffer = lines.pop() || '';

      for (const line of lines) {
        if (line.startsWith('event:')) {
          currentEvent = line.slice('event:'.length).trim();
        } else if (line.startsWith('data:')) {
          currentData.push(line.slice('data:'.length).trim());
        } else if (line.trim() === '') {
          processBufferedEvent();
        }
      }
    }
  };

  return {
    parseEventStream,
    isErrorStreamEvent,
    isInfoStreamEvent,
    isPartialMessageStreamEvent,
    isUnknownStreamEvent,
    isEmailStreamEvent,
    isAiTextStreamEvent,
    getAiTextStreamChunk,
    isEmailPreviewStreamEvent,
    isEmailCompetitorsStreamEvent,
    isOtherStreamEvent,
  };
};

const ParseEventStreamService: ParseEventStreamServiceApi = createParseEventStreamService();

export default ParseEventStreamService;
